import { useState } from 'react';
import { useCreateCashPayment, useGlobalCandidates } from '../../hooks/useBankStatements';
import type { CashPaymentInput, ReconType } from '../../lib/bankStatementsApi';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { useI18n } from '../../contexts/I18nContext';

// ---------------------------------------------------------------------------
// "Adaugă plată cash" modal. Records a manual cash-register entry (both
// directions: Plată/OUTCOME preselected, Încasare/INCOME). The backend stores
// it as a MANUAL_MATCHED line in the synthetic yearly "Registru de casă"
// statement and writes it straight into the Financial ledger, so the amount
// shows up in all statistics immediately.
// ---------------------------------------------------------------------------

type CounterpartyKind = 'person' | 'supplier' | 'client';

interface AddCashPaymentModalProps {
  onClose: () => void;
  /** Called after a successful create (modal is closed by the parent). */
  onCreated: () => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function AddCashPaymentModal({ onClose, onCreated }: AddCashPaymentModalProps) {
  const { t } = useI18n();
  const { data: candidates } = useGlobalCandidates();
  const createMutation = useCreateCashPayment();

  const [direction, setDirection] = useState<ReconType>('OUTCOME');
  const [date, setDate] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState<CounterpartyKind>('supplier');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [counterpartyName, setCounterpartyName] = useState('');
  const [expenseCategoryId, setExpenseCategoryId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const kindOptions: Array<{ value: CounterpartyKind; label: string }> = [
    { value: 'person', label: t('cash.counterparty.person') },
    { value: 'supplier', label: t('cash.counterparty.supplier') },
    { value: 'client', label: t('cash.counterparty.client') },
  ];

  const entityOptions =
    kind === 'person'
      ? (candidates?.people ?? []).map((p) => ({ value: p.id, label: p.name }))
      : kind === 'supplier'
        ? (candidates?.suppliers ?? []).map((s) => ({ value: s.id, label: s.name }))
        : (candidates?.clients ?? []).map((c) => ({ value: c.id, label: c.name }));

  // Real spend categories only — the "income"/"expense" placeholders are the
  // TYPE, not a category (same filter as the reconcile page).
  const categoryOptions = (candidates?.categories ?? [])
    .filter((c) => c.code !== 'income' && c.code !== 'expense')
    .map((c) => ({ value: c.id, label: c.name }));

  const projectOptions = (candidates?.projects ?? []).map((p) => ({
    value: p.id,
    label: p.code ? `${p.code} · ${p.name}` : p.name,
  }));

  function switchKind(next: CounterpartyKind) {
    setKind(next);
    setCounterpartyId('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const parsedAmount = Number(amount.replace(',', '.'));
    if (!(parsedAmount > 0)) {
      setError(t('cash.error.amount'));
      return;
    }
    if (!counterpartyId) {
      setError(t('cash.error.counterparty'));
      return;
    }
    const input: CashPaymentInput = {
      date,
      direction,
      amount: parsedAmount,
      currency: 'RON',
      counterpartyName: counterpartyName.trim() || null,
      matchedPersonId: kind === 'person' ? counterpartyId : null,
      matchedSupplierId: kind === 'supplier' ? counterpartyId : null,
      matchedClientId: kind === 'client' ? counterpartyId : null,
      matchedProjectId: projectId || null,
      expenseCategoryId: direction === 'OUTCOME' ? expenseCategoryId || null : null,
      description: description.trim() || null,
    };
    try {
      await createMutation.mutateAsync(input);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('cash.error.generic'));
    }
  }

  const submitting = createMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-semibold text-gray-900">{t('cash.modalTitle')}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Închide"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
            {/* Direction: Plată (OUTCOME, preselected) / Încasare (INCOME). */}
            <div className="sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('cash.direction')}
              </span>
              <div className="inline-flex rounded-md border border-gray-300 p-0.5">
                {(['OUTCOME', 'INCOME'] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDirection(d)}
                    className={`rounded px-3 py-1.5 text-sm font-medium ${
                      direction === d
                        ? d === 'OUTCOME'
                          ? 'bg-red-500/15 text-red-700'
                          : 'bg-emerald-500/15 text-emerald-700'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {t(`cash.direction.${d}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('cash.date')}
              </label>
              <DateField value={date} onChange={setDate} required className="w-full" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('cash.amount')} (RON)
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                placeholder="0.00"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            {/* Counterparty: exactly one of person / supplier / client. */}
            <div className="sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('cash.counterpartyType')}
              </span>
              <div className="inline-flex rounded-md border border-gray-300 p-0.5">
                {kindOptions.map((k) => (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => switchKind(k.value)}
                    className={`rounded px-3 py-1.5 text-sm font-medium ${
                      kind === k.value
                        ? 'bg-brand-600 text-white'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t(`cash.counterparty.${kind}`)} *
              </span>
              <Select
                label={t(`cash.counterparty.${kind}`)}
                hideLabel
                searchable
                clearable
                value={counterpartyId}
                onChange={setCounterpartyId}
                placeholder="—"
                options={entityOptions}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('cash.counterpartyName')}
              </label>
              <input
                type="text"
                value={counterpartyName}
                onChange={(e) => setCounterpartyName(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            {direction === 'OUTCOME' && (
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-600">
                  {t('cash.category')}
                </span>
                <Select
                  label={t('cash.category')}
                  hideLabel
                  searchable
                  clearable
                  value={expenseCategoryId}
                  onChange={setExpenseCategoryId}
                  placeholder="—"
                  options={categoryOptions}
                />
              </div>
            )}
            <div>
              <span className="mb-1 block text-xs font-medium text-gray-600">
                {t('cash.project')}
              </span>
              <Select
                label={t('cash.project')}
                hideLabel
                searchable
                clearable
                value={projectId}
                onChange={setProjectId}
                placeholder="—"
                options={projectOptions}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t('cash.description')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          {error && <p className="px-5 pb-2 text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              {t('cash.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
